import React from 'react'
import WizTag from '../../../../components/tag/WizTag'
import Text from '../../../../components/text/Text'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {AnnounceContensType} from '../../../../pages/post/announcement/AnnounceCreate'
import moment from 'moment'

interface IProps {
    categories?: string[]
    announceContents: AnnounceContensType
    importantAt?: number
    onClickRemoveCategory?: (category?: string) => void
    onClickRemoveImportantAt?: () => void
}

const AnnouncementMarkdownView: React.FC<IProps> = ({
    categories,
    announceContents,
    importantAt,
    onClickRemoveCategory,
    onClickRemoveImportantAt,
}) => {
    const getCategory = (category: string) => {
        switch (category) {
            case 'announcement':
                return 'Official'
            case 'media':
                return 'Media'
            case 'event':
                return 'Events'
            case 'others':
                return 'Other'
            case 'important':
                return 'Important'
        }
    }
    return (
        <div>
            <div className={'max-w-[692px] bg-white p-[10px]'}>
                <div className={'flex items-center space-x-[6px]'}>
                    {importantAt > moment().unix() && (
                        <WizTag
                            onClick={onClickRemoveImportantAt}
                            textClassName={'bg-tag_red_light text-font_red font-normal cursor-pointer'}>
                            {getCategory('important')}
                        </WizTag>
                    )}
                    {categories?.map(c => (
                        <WizTag
                            key={c}
                            onClick={() => onClickRemoveCategory(c)}
                            textClassName={'bg-icon_bg_black_5 text-font02 font-normal cursor-pointer'}>
                            {getCategory(c)}
                        </WizTag>
                    ))}
                </div>
                <Text className={'text-h2 my-[8px] break-all'}>{announceContents.title}</Text>
                <div className={'flex space-x-[8px]'}>
                    <Text className={'text-body3 text-font03'}>League of Traders EN</Text>
                    <Text className={'text-body3 text-font04'}>|</Text>
                    <Text className={'text-body3 text-font03'}>Sep 01, 2023 14:00</Text>
                </div>
                <div className={'h-[1px] w-full bg-[#00000015] mt-[20px] mb-[20px]'} />
                <div className={'break-all'}>
                    <ReactMarkdown
                        className={''}
                        components={{
                            h1: ({node, ...props}) => <h1 {...props} className={`text-h1 pt-[10.92px] mb-[3.08px]`} />,
                            h2: ({node, ...props}) => <h2 {...props} className={`text-h2 pt-[11.43px] mb-[2.57px]`} />,
                            h3: ({node, ...props}) => <h3 {...props} className={`text-h3 pt-[11.9px] mb-[1.064px]`} />,
                            h4: ({node, ...props}) => <h4 {...props} className={`text-h4 pt-[6.3px] mb-[7.65px]`} />,
                            h5: ({node, ...props}) => <h5 {...props} className={`text-ti1 pt-[6.3px] mb-[7.65px]`} />,
                            h6: ({node, ...props}) => <h6 {...props} className={`text-ti2 pt-[6.3px] mb-[7.65px]`} />,
                            p: ({node, ...props}) => <p {...props} className={`text-body1 pt-[9.24px] mb-[18.76px]`} />,
                            strong: ({node, ...props}) => <strong {...props} className={`font-bold`} />,
                            li: ({node, ordered, ...props}) => {
                                return ordered ? (
                                    <li {...props} className={`list-decimal list-outside `} />
                                ) : (
                                    <li {...props} className={`list-disc list-outside `} />
                                )
                            },
                            ul: ({node, ...props}) => (
                                <ul
                                    {...props}
                                    className={`list-disc list-outside mt-[14px] mb-[11.76px] pt-[2.24px] pl-[40px]`}
                                />
                            ),
                            ol: ({node, ...props}) => (
                                <ol
                                    {...props}
                                    className={`list-decimal list-outside mt-[14px] mb-[11.76px] pt-[2.24px] pl-[40px]`}
                                />
                            ),
                            a: ({node, ...props}) => {
                                return <a {...props} target="_blank" className={'text-font01 underline'} />
                            },
                            code: ({node, inline, className, children, ...props}) => {
                                return inline ? (
                                    <code {...props} className={'bg-bg_gray01 px-[5px] py-[2px] rounded-[8px]'}>
                                        {children}
                                    </code>
                                ) : (
                                    <div
                                        {...props}
                                        className={
                                            'bg-bg_gray01 overflow-auto py-[15px] px-[20px] break-all mb-[18.76px]'
                                        }>
                                        {children}
                                    </div>
                                )
                            },
                            img: ({node, ...props}) => {
                                const metastring = node.properties.alt as string
                                const alt = metastring?.replace(/ *\{[^)]*\} */g, '')
                                const metaWidth = metastring.match(/{([^}]+)x/)
                                const metaHeight = metastring.match(/x([^}]+)}/)
                                const width = metaWidth ? `w-[${metaWidth[1]}px]` : ''
                                const height = metaHeight ? `h-[${metaHeight[1]}px]` : ''
                                const hasAlign = metastring?.toLowerCase().includes('{align:')
                                const align = metastring?.match(/{align: (.*?)}/)?.pop()
                                const getAlign = () => {
                                    if (!hasAlign) return 'flex flex-col items-center'
                                    switch (align) {
                                        case 'left':
                                            return 'flex flex-col items-start'
                                        case 'center':
                                            return 'flex flex-col items-center'
                                        case 'right':
                                            return 'flex flex-col items-end'
                                    }
                                }
                                return (
                                    <span className={`${getAlign()} mb-[30px]`}>
                                        <img {...props} className={`${width} ${height} mb-[10px]`} />
                                        {alt && <Text className={`text-body2 text-font03 text-${align}`}>{alt}</Text>}
                                    </span>
                                )
                            },
                        }}
                        remarkPlugins={[remarkGfm]}>
                        {announceContents.content}
                    </ReactMarkdown>
                </div>
            </div>
        </div>
    )
}
export default AnnouncementMarkdownView
